import type { ApprovalState, PolicyClass } from "./policy.js";
import type { IncidentSeverity } from "./incidents.js";
import { SharedWorkflowStateValue } from "./sharedValues.js";

export enum CompanyToolCategory {
  SourceControl = "source_control",
  CiCd = "ci_cd",
  Identity = "identity",
  Hosting = "hosting",
  DnsEdge = "dns_edge",
  Monitoring = "monitoring",
  Notification = "notification",
  Support = "support",
  CustomerSystem = "customer_system",
  PackageRegistry = "package_registry",
  DataStore = "data_store",
  AiProvider = "ai_provider",
  McpConnector = "mcp_connector",
  InternalTool = "internal_tool"
}

export enum CompanyToolStatus {
  Healthy = "healthy",
  Degraded = "degraded",
  Down = "down",
  Unknown = "unknown",
  Maintenance = "maintenance",
  RateLimited = "rate_limited",
  AuthRequired = "auth_required",
  ApprovalRequired = SharedWorkflowStateValue.ApprovalRequired,
  Disconnected = "disconnected"
}

export enum CompanyToolAuthBoundary {
  PublicStatus = "public_status",
  ServerScopedToken = "server_scoped_token",
  OidcFederated = "oidc_federated",
  McpScoped = "mcp_scoped",
  HumanElevated = "human_elevated",
  BreakGlass = "break_glass"
}

export enum EvidenceConfidence {
  Unverified = "unverified",
  Reported = "reported",
  Corroborated = "corroborated",
  Confirmed = "confirmed"
}

export enum EvidenceRedactionState {
  Redacted = "redacted",
  NoSensitiveData = "no_sensitive_data",
  UnsafeRaw = "unsafe_raw"
}

export enum CustomerImpactLevel {
  None = "none",
  InternalOnly = "internal_only",
  Degraded = "degraded",
  CustomerImpacting = "customer_impacting",
  Critical = "critical"
}

export enum AutonomyDecisionKind {
  Observe = "observe",
  Recommend = "recommend",
  SafeDiagnostic = "safe_diagnostic",
  BoundedRecovery = "bounded_recovery",
  ApprovalRequired = SharedWorkflowStateValue.ApprovalRequired,
  HumanRequired = "human_required",
  Denied = "denied"
}

export enum HumanDecisionKind {
  ApprovalRequired = SharedWorkflowStateValue.ApprovalRequired,
  ElevatedAccessRequired = "elevated_access_required",
  CustomerCommsReview = "customer_comms_review",
  EvidenceGap = "evidence_gap",
  EscalationTimer = "escalation_timer",
  ResponderAcknowledgement = "responder_acknowledgement",
  CriticalActionReady = "critical_action_ready",
  VerificationFailed = "verification_failed",
  ToolUnavailable = "tool_unavailable"
}

export enum HumanDecisionPriority {
  Low = "low",
  Medium = "medium",
  High = "high",
  Critical = "critical"
}

export enum TypedActionExecutor {
  Api = "api",
  Worker = "worker",
  Mcp = "mcp",
  Human = "human"
}

export enum ReplayScenarioKind {
  FakeIncident = "fake_incident",
  HistoricalIncident = "historical_incident",
  GameDay = "game_day",
  LlmRegression = "llm_regression"
}

export interface CompanyToolOwner {
  readonly ownerId: string;
  readonly displayName: string;
  readonly escalationRef: string;
}

export interface CompanyTool {
  readonly id: string;
  readonly displayName: string;
  readonly category: CompanyToolCategory;
  readonly status: CompanyToolStatus;
  readonly statusSource: string;
  readonly owner: CompanyToolOwner;
  readonly backupOwner: CompanyToolOwner | null;
  readonly authBoundary: CompanyToolAuthBoundary;
  readonly credentialScope: string;
  readonly allowedPolicyClasses: readonly PolicyClass[];
  readonly allowedActionIds: readonly string[];
  readonly relatedServiceIds: readonly string[];
  readonly activeIncidentIds: readonly string[];
  readonly lastCheckedAt: string | null;
  readonly runbookIds: readonly string[];
}

export interface ServiceDependency {
  readonly serviceId: string;
  readonly dependsOnServiceId: string;
  readonly critical: boolean;
}

export interface CustomerAudience {
  readonly audienceRef: string;
  readonly displayName: string;
  readonly locale: string;
  readonly notificationPolicyId: string;
}

export interface CustomerImpactSummary {
  readonly level: CustomerImpactLevel;
  readonly serviceId: string;
  readonly affectedAudienceRefs: readonly string[];
  readonly affectedCustomerCount: number;
  readonly severity: IncidentSeverity;
  readonly summary: string;
}

export interface EvidenceCitation {
  readonly evidenceId: string;
  readonly quote: string | null;
}

export interface EvidenceGraphNode {
  readonly id: string;
  readonly incidentId: string;
  readonly source: string;
  readonly label: string;
  readonly redactedSummary: string;
  readonly confidence: EvidenceConfidence;
  readonly redactionState: EvidenceRedactionState;
  readonly relatedToolIds: readonly string[];
  readonly relatedServiceIds: readonly string[];
  readonly observedAt: string;
}

export interface EvidenceGraphEdge {
  readonly fromEvidenceId: string;
  readonly toEvidenceId: string;
  readonly relation: "corroborates" | "contradicts" | "supersedes" | "caused_by" | "depends_on";
}

export interface ActionRollbackPlan {
  readonly available: boolean;
  readonly summary: string;
  readonly typedActionId: string | null;
}

export interface ActionVerificationCheck {
  readonly checkId: string;
  readonly summary: string;
  readonly required: boolean;
}

export interface TypedActionDefinition {
  readonly id: string;
  readonly title: string;
  readonly policyClass: PolicyClass;
  readonly executor: TypedActionExecutor;
  readonly toolId: string;
  readonly dryRunSupported: boolean;
  readonly requiresElevatedAccess: boolean;
  readonly rollback: ActionRollbackPlan;
  readonly verification: ActionVerificationCheck;
}

export interface TypedActionInvocation {
  readonly id: string;
  readonly actionId: string;
  readonly incidentId: string;
  readonly toolId: string;
  readonly evidenceCitations: readonly EvidenceCitation[];
  readonly approvalState: ApprovalState;
  readonly auditCorrelationId: string;
  readonly requestedAt: string;
}

export interface HumanDecisionQueueItem {
  readonly id: string;
  readonly incidentId: string;
  readonly kind: HumanDecisionKind;
  readonly priority: HumanDecisionPriority;
  readonly title: string;
  readonly reason: string;
  readonly actionId: string | null;
  readonly toolId: string | null;
  readonly evidenceIds: readonly string[];
  readonly approverRole: string | null;
  readonly expiresAt: string | null;
}

export interface AutonomyDecision {
  readonly kind: AutonomyDecisionKind;
  readonly reason: string;
  readonly actionId: string | null;
  readonly toolId: string | null;
  readonly evidenceIds: readonly string[];
  readonly decisionItems: readonly HumanDecisionQueueItem[];
}

export interface ReplayScenario {
  readonly id: string;
  readonly kind: ReplayScenarioKind;
  readonly title: string;
  readonly incidentId: string;
  readonly expectedAutonomyDecision: AutonomyDecisionKind;
  readonly fixtureRefs: readonly string[];
}

export interface SimulationRun {
  readonly id: string;
  readonly scenarioId: string;
  readonly passed: boolean;
  readonly autonomyDecision: AutonomyDecisionKind;
  readonly createdAt: string;
}

export interface LlmReliabilityEval {
  readonly id: string;
  readonly promptVersion: string;
  readonly modelRef: string;
  readonly scenarioId: string;
  readonly evidenceCitationPass: boolean;
  readonly toolSchemaPass: boolean;
  readonly redactionPass: boolean;
  readonly createdAt: string;
}

export interface IncidentResolutionMemory {
  readonly incidentId: string;
  readonly rootCause: string;
  readonly remediationProofEvidenceIds: readonly string[];
  readonly customerSafeSummary: string;
  readonly runbookLearningCandidate: string | null;
  readonly createdAt: string;
}

export const companyToolCategories = Object.values(CompanyToolCategory);
export const companyToolStatuses = Object.values(CompanyToolStatus);
export const companyToolAuthBoundaries = Object.values(CompanyToolAuthBoundary);
export const evidenceConfidences = Object.values(EvidenceConfidence);
export const evidenceRedactionStates = Object.values(EvidenceRedactionState);
export const customerImpactLevels = Object.values(CustomerImpactLevel);
export const autonomyDecisionKinds = Object.values(AutonomyDecisionKind);
export const humanDecisionKinds = Object.values(HumanDecisionKind);
export const humanDecisionPriorities = Object.values(HumanDecisionPriority);
export const typedActionExecutors = Object.values(TypedActionExecutor);
export const replayScenarioKinds = Object.values(ReplayScenarioKind);

export function isCompanyToolCategory(value: string | null | undefined): value is CompanyToolCategory {
  return value !== null && value !== undefined && companyToolCategories.some((category) => category === value);
}

export function isCompanyToolStatus(value: string | null | undefined): value is CompanyToolStatus {
  return value !== null && value !== undefined && companyToolStatuses.some((status) => status === value);
}

export function isCompanyToolAuthBoundary(value: string | null | undefined): value is CompanyToolAuthBoundary {
  return value !== null && value !== undefined && companyToolAuthBoundaries.some((boundary) => boundary === value);
}

export function isEvidenceConfidence(value: string | null | undefined): value is EvidenceConfidence {
  return value !== null && value !== undefined && evidenceConfidences.some((confidence) => confidence === value);
}

export function isEvidenceRedactionState(value: string | null | undefined): value is EvidenceRedactionState {
  return value !== null && value !== undefined && evidenceRedactionStates.some((state) => state === value);
}

export function isCustomerImpactLevel(value: string | null | undefined): value is CustomerImpactLevel {
  return value !== null && value !== undefined && customerImpactLevels.some((level) => level === value);
}

export function isAutonomyDecisionKind(value: string | null | undefined): value is AutonomyDecisionKind {
  return value !== null && value !== undefined && autonomyDecisionKinds.some((kind) => kind === value);
}

export function isHumanDecisionKind(value: string | null | undefined): value is HumanDecisionKind {
  return value !== null && value !== undefined && humanDecisionKinds.some((kind) => kind === value);
}

export function isHumanDecisionPriority(value: string | null | undefined): value is HumanDecisionPriority {
  return value !== null && value !== undefined && humanDecisionPriorities.some((priority) => priority === value);
}

export function isTypedActionExecutor(value: string | null | undefined): value is TypedActionExecutor {
  return value !== null && value !== undefined && typedActionExecutors.some((executor) => executor === value);
}

export function isReplayScenarioKind(value: string | null | undefined): value is ReplayScenarioKind {
  return value !== null && value !== undefined && replayScenarioKinds.some((kind) => kind === value);
}
